import { z } from 'zod';
import { CRM_MODULES, EMAIL_DIRECTIONS, type CrmModule, type EmailDirection } from '../enums.js';
import {
  emailSchema,
  optionalEnum,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { relatedEntitySchema, type RelatedEntity } from './records.js';

/**
 * Email
 * =====
 * WHN does not run a mail server. It composes a message from a template,
 * hands it to whatever transport is configured, and — always — writes it to the
 * record's timeline. The log is the point: "what did we last say to this
 * customer" is the question a CRM has to answer, whether the message left
 * through WHN or was pasted in after the fact.
 */

export const createEmailTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  subject: z.string().trim().min(1, 'Required').max(300),
  bodyHtml: z.string().trim().min(1, 'Required').max(100_000),
  bodyText: optionalText(100_000),
  /** Null means the template is offered everywhere; '' is the select's default. */
  module: optionalEnum(CRM_MODULES),
  isActive: z.coerce.boolean().default(true),
});
export type CreateEmailTemplateInput = z.infer<typeof createEmailTemplateSchema>;

export const updateEmailTemplateSchema = toPatchSchema(createEmailTemplateSchema);
export type UpdateEmailTemplateInput = z.infer<typeof updateEmailTemplateSchema>;

export const listEmailTemplatesQuerySchema = paginationQuerySchema.extend({
  module: z.enum(CRM_MODULES).optional(),
  isActive: z.stringbool().optional(),
  search: z.string().trim().max(120).optional(),
});
export type ListEmailTemplatesQuery = z.infer<typeof listEmailTemplatesQuerySchema>;

export interface EmailTemplateSummary {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  module: CrmModule | null;
  /** Tokens found in the subject and body, cached at save time. */
  mergeFields: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Preview resolves a template against a real record so the sender sees the
 * merged text — and, more usefully, the list of tokens that resolved to
 * nothing — before anything is sent.
 */
export const previewEmailSchema = z.object({
  templateId: uuidSchema.optional(),
  subject: z.string().max(300).optional(),
  bodyHtml: z.string().max(100_000).optional(),
  related: relatedEntitySchema,
});
export type PreviewEmailInput = z.infer<typeof previewEmailSchema>;

export interface EmailPreview {
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  /** Suggested recipients drawn from the record itself. */
  to: string[];
  /** Tokens that resolved to nothing — shown as a warning, not an error. */
  unresolved: string[];
}

export const sendEmailSchema = z.object({
  templateId: uuidSchema.optional(),
  related: relatedEntitySchema,
  to: z.array(emailSchema).min(1, 'Add at least one recipient').max(25),
  cc: z.array(emailSchema).max(25).default([]),
  bcc: z.array(emailSchema).max(25).default([]),
  subject: z.string().trim().min(1, 'Required').max(300),
  bodyHtml: z.string().trim().min(1, 'Required').max(100_000),
  bodyText: optionalText(100_000),
  /**
   * LOG records a message sent elsewhere; SEND hands it to the configured
   * transport. Both write the same timeline entry.
   */
  mode: z.enum(['SEND', 'LOG']).default('SEND'),
});
export type SendEmailInput = z.infer<typeof sendEmailSchema>;

export const listEmailsQuerySchema = paginationQuerySchema.extend({
  leadId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  contactId: uuidSchema.optional(),
  dealId: uuidSchema.optional(),
});
export type ListEmailsQuery = z.infer<typeof listEmailsQuerySchema>;

export const EMAIL_STATUSES = ['QUEUED', 'SENT', 'FAILED', 'LOGGED'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export interface EmailMessageSummary {
  id: string;
  direction: EmailDirection;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  fromEmail: string;
  to: string[];
  cc: string[];
  bcc: string[];
  status: EmailStatus;
  error: string | null;
  sentAt: string | null;
  related: RelatedEntity | null;
  templateId: string | null;
  sender: { id: string; name: string } | null;
  createdAt: string;
}

export const EMAIL_DIRECTION_VALUES = EMAIL_DIRECTIONS;
